import type { ConfigureIntegrationBody, Integration } from '@hostesa/api-client';
import { useConfigureIntegration } from '@hostesa/api-client';
import { useT } from '@hostesa/i18n';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  toast,
} from '@hostesa/ui';
import { type FormEvent, useState } from 'react';
import { useImpersonationGuard } from '../../lib/impersonation-guard';
import { namespace } from './i18n';

export interface IntegrationConfigFormProps {
  tenantId: string;
  hotelId: string;
  /** Called with the configured integration on success (e.g. to track its id). */
  onConfigured?: (integration: Integration) => void;
}

/**
 * Write-only PMS configuration form.
 *
 * Credentials are WRITE-ONLY: the secret inputs are `type="password"`, never
 * pre-populated from any stored value (no endpoint returns secrets), and are
 * cleared on success so a saved secret is never echoed back. The mutation is
 * NON-OPTIMISTIC (provider configuration is in the never-optimistic category):
 * the form only reflects success after the server confirms.
 */
export function IntegrationConfigForm({
  tenantId,
  hotelId,
  onConfigured,
}: IntegrationConfigFormProps) {
  const { t } = useT(namespace);
  const { t: tShared } = useT('platform');
  const { restricted } = useImpersonationGuard();
  const configure = useConfigureIntegration(tenantId);

  const [provider, setProvider] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    // Configuring a PMS connection is blocked during impersonation (the backend
    // rejects it too); this stops an operator firing it by reflex.
    if (restricted) return;

    if (provider.trim().length === 0) {
      setError(t('config.providerRequired'));
      return;
    }
    if (apiKey.trim().length === 0) {
      setError(t('config.apiKeyRequired'));
      return;
    }

    // Assemble the write-only credentials bag. The generated DTO types
    // `credentials` as an opaque record; we never read it back.
    const credentials: Record<string, string> = { apiKey };
    if (apiSecret.length > 0) credentials.apiSecret = apiSecret;
    if (baseUrl.trim().length > 0) credentials.baseUrl = baseUrl.trim();

    const body = {
      hotelId,
      provider: provider.trim(),
      credentials,
    } as unknown as ConfigureIntegrationBody;

    configure.mutate(body, {
      onSuccess: (integration) => {
        // Clear the secret fields so a saved credential is never displayed.
        setApiKey('');
        setApiSecret('');
        toast.success(t('config.successToast'));
        onConfigured?.(integration);
      },
      onError: () => {
        const message = t('config.errorToast');
        setError(message);
        toast.error(message);
      },
    });
  };

  const submitting = configure.isPending;

  return (
    <Card data-testid="integration-config-form-card">
      <CardHeader>
        <CardTitle>{t('config.title')}</CardTitle>
        <CardDescription>{t('config.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          data-testid="integration-config-form"
          onSubmit={onSubmit}
          className="flex flex-col gap-4"
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="integration-provider-input">{t('config.providerLabel')}</Label>
            <Input
              id="integration-provider-input"
              data-testid="integration-provider-input"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              placeholder={t('config.providerPlaceholder')}
              autoComplete="off"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="integration-api-key-input">{t('config.apiKeyLabel')}</Label>
            <Input
              id="integration-api-key-input"
              data-testid="integration-api-key-input"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={t('config.apiKeyPlaceholder')}
              autoComplete="new-password"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="integration-api-secret-input">{t('config.apiSecretLabel')}</Label>
            <Input
              id="integration-api-secret-input"
              data-testid="integration-api-secret-input"
              type="password"
              value={apiSecret}
              onChange={(e) => setApiSecret(e.target.value)}
              placeholder={t('config.apiSecretPlaceholder')}
              autoComplete="new-password"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="integration-base-url-input">{t('config.baseUrlLabel')}</Label>
            <Input
              id="integration-base-url-input"
              data-testid="integration-base-url-input"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder={t('config.baseUrlPlaceholder')}
              autoComplete="off"
            />
          </div>

          <p className="text-xs text-[var(--color-stone-mid)]">{t('config.writeOnlyNote')}</p>

          {error && (
            <p
              data-testid="integration-config-error"
              role="alert"
              className="text-sm text-[var(--color-alert)]"
            >
              {error}
            </p>
          )}

          {restricted && (
            <p
              data-testid="integration-config-impersonation-block"
              role="note"
              className="text-sm text-[var(--color-stone-mid)]"
            >
              {tShared('impersonationRestriction')}
            </p>
          )}

          <Button
            type="submit"
            data-testid="integration-config-submit-btn"
            disabled={submitting || restricted}
          >
            {submitting ? t('config.submitting') : t('config.submitBtn')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
